import {
  BriefcaseIcon,
  DocumentTextIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../Utils";
import {
  requestListBusinessServiceCategories,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { useNavigate } from "react-router-dom";
import { EmptyList } from "../../Components/EmptyList";

const BusinessServiceList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useBusinessServiceDispatch();
  const { businessServiceCategories, loading, error } =
    useBusinessServiceState();

  useEffect(() => {
    requestListBusinessServiceCategories(dispatch, {
      page: 1,
      limit: 10,
    });
  }, [dispatch]);

  const services = useMemo(() => {
    return businessServiceCategories
      .map((category) => {
        switch (category.type) {
          case "BUSINESS_ESTABLISHMENT":
            return {
              title: t("businessLicenses.establishmentTitle"),
              href: `/business-service/${category.id}`,
              icon: OfficeBuildingIcon,
              iconForeground: "text-blue-600",
              iconBackground: "bg-blue-50",
              description: t("businessLicenses.establishmentDesc"),
            };
          case "DEED_AMENDMENT":
            return {
              title: t("businessLicenses.deedAmendmentTitle"),
              href: `/business-service/${category.id}`,
              icon: DocumentTextIcon,
              iconForeground: "text-teal-600",
              iconBackground: "bg-teal-50",
              description: t("businessLicenses.deedAmendmentDesc"),
            };
          default:
            return {
              title: category.name,
              href: `/business-service/${category.id}`,
              icon: BriefcaseIcon,
              iconForeground: "text-gray-600",
              iconBackground: "bg-gray-50",
              description: category.description,
            };
        }
      })
      .filter(Boolean);
  }, [businessServiceCategories, t]);

  const ServiceSkeleton = ({ length }) => {
    return (
      <div className="rounded-lg bg-gray-200 overflow-hidden shadow divide-y divide-gray-200 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px">
        {Array.from({ length }).map((_, serviceIdx) => (
          <div
            key={serviceIdx}
            className={classNames(
              serviceIdx === 0
                ? "rounded-tl-lg rounded-tr-lg sm:rounded-tr-none"
                : "",
              serviceIdx === 1 ? "sm:rounded-tr-lg" : "",
              serviceIdx === length - 2 ? "sm:rounded-bl-lg" : "",
              serviceIdx === length - 1
                ? "rounded-bl-lg rounded-br-lg sm:rounded-bl-none"
                : "",
              "relative bg-white p-6"
            )}
          >
            <div className="flex items-center justify-center h-12 w-12 bg-gray-300 rounded-lg mb-4 animate-pulse">
              <div className="h-6 w-6 bg-gray-500 rounded-full"></div>
            </div>
            <div className="mt-8">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2 animate-pulse"></div>
              <div className="h-3 bg-gray-300 rounded w-5/6 animate-pulse"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <ServiceSkeleton length={2} />;
  }

  if (error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error.message}
          errors={error.errors}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-wrap sm:w-full justify-between gap-4">
      <div className="flex flex-col gap-4 justify-around sm:flex-row">
        <div className="w-full justify-start sm:justify-end">
          <Button
            onClick={() => navigate("/business-service/order")}
            title="viewOrderHistory"
          />
        </div>
      </div>
      {services.length > 0 ? (
        <div className="rounded-lg bg-gray-200 overflow-hidden shadow divide-y divide-gray-200 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px">
          {services.map((service, serviceIdx) => (
            <div
              key={serviceIdx}
              className={classNames(
                serviceIdx === 0
                  ? "rounded-tl-lg rounded-tr-lg sm:rounded-tr-none"
                  : "",
                serviceIdx === 1 ? "sm:rounded-tr-lg" : "",
                serviceIdx === services.length - 2 ? "sm:rounded-bl-lg" : "",
                serviceIdx === services.length - 1
                  ? "rounded-bl-lg rounded-br-lg sm:rounded-bl-none"
                  : "",
                "relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500"
              )}
            >
              <div>
                <span
                  className={classNames(
                    service.iconBackground,
                    service.iconForeground,
                    "rounded-lg inline-flex p-3 ring-4 ring-white"
                  )}
                >
                  <service.icon className="h-6 w-6" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium">
                  <a href={service.href} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
                    <span className="absolute inset-0" aria-hidden="true" />
                    {service.title}
                  </a>
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {service.description}
                </p>
              </div>
              <span
                className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white col-span-12 rounded-lg p-4">
          <EmptyList title="noServicesAvailable" />
        </div>
      )}
    </div>
  );
};

export default BusinessServiceList;
